import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../axiosConfig';
console.log('🔥 Loaded: Dashboard Layout');
const ClientDashboard = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [clientData, setClientData] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      const token = localStorage.getItem('magicToken');
      if (!token) {
        console.error('❌ No magic token found in localStorage');
        navigate('/expired');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };

      try {
        const tokenDetailRes = await axios.get(`/token-detail/${token}/`, { headers });
        const client = tokenDetailRes.data.client;
        const project = tokenDetailRes.data.project;

        if (!client || !project) {
          console.warn('⚠️ Missing client or project from token-detail');
          navigate('/expired');
          return;
        }

        localStorage.setItem('clientData', JSON.stringify(client));
        localStorage.setItem('projectData', JSON.stringify(project));
        setClientData(client);
        setProjectData(project);

        const pid = project.id;

        const messagesRes = await axios.get(`/messages/?project=${pid}`, { headers });
        setMessages(Array.isArray(messagesRes.data) ? messagesRes.data : []);

        const filesRes = await axios.get(`/uploads/?project=${pid}`, { headers });
        setUploadedFiles(Array.isArray(filesRes.data) ? filesRes.data : []);
      } catch (err) {
        console.error('❌ Dashboard load error:', err);
        navigate('/expired');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [projectId, navigate]);

  const sendMessage = async () => {
    const token = localStorage.getItem('magicToken');
    if (!newMessage.trim() || !token || !projectData) return;

    try {
      const payload = {
        sender_name: clientData.name || 'Client',
        content: newMessage,
        project: projectData.id,
      };

      const headers = { Authorization: `Bearer ${token}` };

      const res = await axios.post('/messages/', payload, { headers });
      setMessages((prev) => [res.data, ...prev]);
      setNewMessage('');
    } catch (err) {
      console.error('❌ Message send error:', err);
    }
  };

  if (loading) return <p className="text-center">Loading dashboard...</p>;
  if (!clientData || !projectData) return <p className="text-center">Dashboard data missing.</p>;

  return (
    <div className="bg-[#FFF8EC] min-h-screen p-6 text-[#111827] font-[Inter]">
      <h2 className="text-2xl font-[Poppins] font-bold mb-4 text-[#F95F53]">
        Welcome, {clientData.name} 🐝
      </h2>

      <div className="bg-white rounded-xl shadow p-4 border border-[#E5E7EB] mb-6">
        <p className="mb-1 font-medium">
          Project: <span className="text-[#111827]">{projectData.name}</span>
        </p>
        <p className="text-sm text-gray-600">Description: {projectData.description}</p>
      </div>

      <h3 className="text-xl font-semibold font-[Poppins] mb-2 text-[#F95F53]">Messages</h3>
      <div className="mb-4 flex gap-2">
        <input
          type="text"
          className="flex-1 border rounded px-2 py-1 text-sm"
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
        />
        <button
          onClick={sendMessage}
          className="bg-blue-600 text-white px-4 py-1 rounded hover:bg-blue-700 text-sm"
        >
          Send
        </button>
      </div>

      <ul className="space-y-2 mb-6">
        {messages.map(msg => (
          <li key={msg.id} className="bg-white p-3 shadow rounded border border-[#E5E7EB]">
            <p className="text-xs text-gray-500 mb-1">
              {msg.sender_name} • {new Date(msg.created_at).toLocaleString()}
            </p>
            <p className="text-sm">{msg.content}</p>
          </li>
        ))}
      </ul>

      <h3 className="text-xl font-semibold font-[Poppins] mb-2 text-[#F95F53]">Uploaded Files</h3>
      <ul className="space-y-2">
        {uploadedFiles.length === 0 ? (
          <li className="text-sm text-gray-500">No files uploaded yet 🐷🍯</li>
        ) : (
          uploadedFiles.map(file => (
            <li key={file.id} className="bg-white p-3 shadow rounded border border-[#E5E7EB]">
              <a
                href={file.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#F95F53] font-medium hover:underline"
              >
                {file.filename}
              </a>
              <p className="text-xs text-gray-500">
                Uploaded on {new Date(file.uploaded_at).toLocaleDateString()}
              </p>
            </li>
          ))
        )}
      </ul>
    </div>
  );
};

export default ClientDashboard;